'use client';

import { FunctionComponent, useState, useRef } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import XForm from '@xFrame4/components/common/XForm';
import FieldEditor from '@xFrame4/components/common/FieldEditor';
import { FieldEditorLabelPosition, FieldEditorType } from '@xFrame4/components/common/FieldEditorProps';
import EmailOptInForm from '@business/contact/EmailOptInForm';
import ALink from '@xFrame4/components/next/ALink';
import AppTracker from '@components/tracking/AppTracker';

export interface EmailOptInFormProps
{
    emailOptInForm: any; // JSON-serialized EmailOptInForm object
    language: string;
}

/**
 * Component: email opt-in form component that follows the ContactForm pattern
 */
const EmailOptInFormComponent: FunctionComponent<EmailOptInFormProps> = (props) =>
{
    const t = useTranslate();
    const formRef = useRef<any>(null);

    // Convert back from JSON to EmailOptInForm object
    const emailOptInForm = EmailOptInForm.createFromJson(props.emailOptInForm) as EmailOptInForm;
    const formTranslation = emailOptInForm.getTranslation(props.language);

    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [needsConfirmation, setNeedsConfirmation] = useState<boolean>(false);

    /** Submit the form. */
    const submitForm = () =>
    {
        if (formRef.current != null) (formRef.current as any).submit();
    }

    /** Validate the form. */
    const validateForm = () =>
    {
        let errors: string[] = [];

        if (!email.trim())
        {
            errors.push(t('EMAIL_IS_REQUIRED', props.language));
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        {
            errors.push(t('EMAIL_IS_INVALID', props.language));
        }

        if (emailOptInForm.askForName && !name.trim())
        {
            errors.push(t('NAME_IS_REQUIRED', props.language));
        }

        setErrorMessages(errors);
        
        return errors.length === 0;
    }

    /** Save the email opt-in form entry to the database. */
    const saveEmailOptInFormEntry = async () =>
    {
        setIsSubmitting(true);
        try
        {
            // Get browser information
            const userAgent = navigator.userAgent;

            // Create the entry using the business entity method
            const result = await emailOptInForm.createEntry(
                email,
                emailOptInForm.askForName ? name : undefined,
                emailOptInForm.askForPhone ? phone : undefined,
            );

            if (result.success)
            {
                // Track the email opt-in form submission
                AppTracker.trackEmailOptInFormSubmit(
                    email,
                    emailOptInForm.askForName ? name : undefined,
                    emailOptInForm.askForPhone ? phone : undefined
                );

                // Reset form
                setEmail('');
                setName('');
                setPhone('');
                setErrorMessages([]);

                if (result.needsConfirmation)
                {
                    let confirmationEmailSentMessage = formTranslation?.formConfirmationEmailSentMessage ?? '';
                    if (confirmationEmailSentMessage.length === 0) confirmationEmailSentMessage = t('EMAIL_OPT_IN_CONFIRMATION_EMAIL_SENT', props.language);
                    setSuccessMessage(confirmationEmailSentMessage);
                    setNeedsConfirmation(true);
                }
                else
                {
                    let successMessage = formTranslation?.formSuccessMessage ?? '';
                    if (successMessage.length === 0) successMessage = t('EMAIL_OPT_IN_SUCCESS_MESSAGE', props.language);
                    setSuccessMessage(successMessage);
                }
            }
            else
            {
                setErrorMessages([result.message || t('EMAIL_OPT_IN_ERROR_MESSAGE', props.language)]);
            }
        }
        catch (error)
        {
            console.error('Error submitting email opt-in form:', error);
            setErrorMessages([t('EMAIL_OPT_IN_ERROR_MESSAGE', props.language)]);
        }
        finally
        {
            setIsSubmitting(false);
        }
    }

    /** Render */
    return (
        <div className="email-opt-in-form">
            {!successMessage ? (
                <XForm
                    ref={formRef}
                    onValidate={() => validateForm()}
                    onSubmit={() => saveEmailOptInFormEntry()}
                >
                    <FieldEditor
                        type={FieldEditorType.Email}
                        field="email"
                        labelPosition={FieldEditorLabelPosition.Hidden}
                        placeholder={formTranslation?.formEmailLabel || t('EMAIL', props.language)}
                        isRequired={true}
                        className="mb-3"
                        value={email}
                        onValueChanged={(_, value) => setEmail(value)}
                    />

                    {emailOptInForm.askForName && (
                        <FieldEditor
                            type={FieldEditorType.Text}
                            field="name"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={formTranslation?.formNameLabel || t('NAME', props.language)}
                            isRequired={true}
                            className="mb-3"
                            value={name}
                            onValueChanged={(_, value) => setName(value)}
                        />
                    )}

                    {emailOptInForm.askForPhone && (
                        <FieldEditor
                            type={FieldEditorType.Text}
                            field="phone"
                            labelPosition={FieldEditorLabelPosition.Hidden}
                            placeholder={formTranslation?.formPhoneLabel || t('PHONE', props.language)}
                            isRequired={true}
                            className="mb-3"
                            value={phone}
                            onValueChanged={(_, value) => setPhone(value)}
                        />
                    )}

                    <div className="messages mb-3">
                        {errorMessages.length > 0 &&
                            <div className="error-messages">
                                {errorMessages.map((errorMessage, index) => (
                                    <Alert key={index} variant="danger">{errorMessage}</Alert>
                                ))}
                            </div>
                        }
                    </div>

                    <Button
                        variant="primary"
                        onClick={submitForm}
                        disabled={isSubmitting}
                        className="btn-lg rounded-3 px-4 py-2 fw-semibold text-uppercase"
                    >
                        {isSubmitting ? t('SENDING', props.language) : (formTranslation?.formSubmitButtonText || t('SUBSCRIBE', props.language))}
                    </Button>
                </XForm>
            ) : (
                <div className="email-opt-in-success text-center p-4">
                    <Alert variant="success" className="mb-4">{successMessage}</Alert>
                    {!needsConfirmation && (
                        <div className="mt-3">
                            {formTranslation?.redirectUrl ? (
                                <ALink href={formTranslation.redirectUrl} className="btn btn-primary rounded-3 px-4 py-2 fw-semibold text-uppercase">
                                    {t('CONTINUE', props.language)}
                                </ALink>
                            ) : (
                                <ALink href={`/${props.language}`} className="btn btn-primary rounded-3 px-4 py-2 fw-semibold text-uppercase">
                                    {t('GO_TO_HOMEPAGE', props.language)}
                                </ALink>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EmailOptInFormComponent;